import React, { useEffect, useState } from 'react';
import {
  Box, Card, Typography, IconButton, Tooltip, Button,
} from '@mui/material';
import { CONTENT_TYPES } from '../../constants/contentTypes';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import { useLocalization } from '../../context/LocalizationContext';
import ApprovalStatusBadge from '../approval/ApprovalStatusBadge';
import WithdrawButton from '../approval/WithdrawButton';
import SubmitForApprovalButton from '../approval/SubmitForApprovalButton';

const viewBtnSx = {
  borderColor: '#3b82f6', color: '#1d4ed8', bgcolor: '#eff6ff',
  borderRadius: '8px', px: 1.5, py: 0.5, fontSize: 11, fontWeight: 700,
  textTransform: 'uppercase', letterSpacing: 0.4, whiteSpace: 'nowrap',
  boxShadow: 'none',
  '&:hover': { bgcolor: '#dbeafe', borderColor: '#2563eb', boxShadow: 'none' },
};

const pillSx = {
  display: 'inline-flex', alignItems: 'center', gap: 0.5,
  fontSize: 11, fontWeight: 700, color: '#475569',
  bgcolor: '#f1f5f9', borderRadius: '6px', px: 1, py: 0.5, lineHeight: 1.4,
};

const formatSchedule = (start, end) => {
  if (!start) return '—';
  const s = new Date(start);
  const opts = { month: 'short', day: '2-digit', hour: 'numeric', minute: '2-digit' };
  const startText = s.toLocaleString('en-US', opts);
  if (!end) return startText;
  const e = new Date(end);
  const endText = e.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${startText}–${endText}`;
};

// Resolves a lookup id (e.g. eventFormatId) to its display name; falls back to the raw id
// (never blank) if the lookup hasn't loaded yet or no longer matches anything.
const lookupName = (list, id, nameKey) => {
  if (id == null) return null;
  const match = list.find((item) => String(item.id) === String(id));
  return match ? match[nameKey] : id;
};

const EventCard = ({
  event,
  eventFormats = [],
  onEdit,
  onDelete,
  onProposeDelete,
  onViewProposal,
  onViewReadonly,
  onSuccess,
  preview = false,
  showActions = true,
}) => {
  const { locale } = useLocalization();
  const name =
    event.localizedName?.values?.find((v) => v.localeId === locale)?.value ||
    event.localizedName?.values?.[0]?.value ||
    '';

  const deliveryUrl = event.hostingStreamUrl || event.eventUrl || '';

  const hasPersonality = Boolean(event.personalityName);

  // A broken/unreachable photo CDN link shouldn't leave a blank/broken-image banner —
  // fall back to the plain text "Featuring" row instead.
  const [imgFailed, setImgFailed] = useState(false);
  useEffect(() => { setImgFailed(false); }, [event.personalityPhotoLink]);
  const hasPersonalityPhoto = hasPersonality && Boolean(event.personalityPhotoLink) && !imgFailed;

  const formatLabel = lookupName(eventFormats, event.eventFormatId, 'Name');

  const isLocked      = event.approvalStatus === 'pending' || event.approvalStatus === 'rejected';
  const canDirectEdit = event.contentStatus === 'draft' && !event.approvalId && !isLocked;

  const handleOpenUrl = (e) => {
    if (preview || !deliveryUrl) return;
    e.stopPropagation();
    window.open(deliveryUrl, '_blank', 'noopener,noreferrer');
  };

  const editTooltip = event.approvalStatus === 'pending'
    ? 'Withdraw to edit'
    : event.approvalStatus === 'rejected'
      ? 'Dismiss to edit'
      : 'Edit Event';

  const deleteDisabled = isLocked;

  const deleteTooltip = event.approvalStatus === 'pending'
    ? 'Withdraw proposal before deleting'
    : event.approvalStatus === 'rejected'
      ? 'Dismiss proposal before deleting'
      : canDirectEdit ? 'Delete Event' : 'Propose Deletion';

  const handleDeleteClick = (e) => {
    e.currentTarget.blur();
    e.stopPropagation();
    if (canDirectEdit) {
      onDelete(event);
    } else {
      onProposeDelete?.(event);
    }
  };

  const liveBadge = (
    <Box
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.6,
        bgcolor: event.isLive ? '#dc2626' : '#475569',
        color: 'white', px: 1.1, py: 0.4, borderRadius: '999px',
        fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.6,
      }}
    >
      {event.isLive && (
        <Box
          sx={{
            width: 6, height: 6, borderRadius: '50%', bgcolor: 'white',
            '@keyframes eventCardLivePulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.35 } },
            animation: 'eventCardLivePulse 1.4s ease-in-out infinite',
          }}
        />
      )}
      {event.isLive ? 'Live' : 'Recorded'}
    </Box>
  );

  return (
    <Card
      sx={{
        bgcolor: 'white',
        borderRadius: '16px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        border: '2px solid transparent',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        transition: 'all 0.2s ease',
        ...(!preview && {
          '&:hover': {
            boxShadow: '0 8px 24px rgba(10,50,109,0.15)',
            borderColor: '#0a326d',
            transform: 'translateY(-2px)',
          },
          '&:hover .event-card-photo': { transform: 'scale(1.06)' },
        }),
      }}
    >
      {hasPersonalityPhoto && (
        <Box sx={{ position: 'relative', height: { xs: 150, sm: 175, md: 190 }, overflow: 'hidden', bgcolor: '#0a326d' }}>
          <Box
            component="img"
            className="event-card-photo"
            src={event.personalityPhotoLink}
            alt={event.personalityName || 'Personality'}
            onError={() => setImgFailed(true)}
            sx={{
              position: 'absolute', inset: 0, width: '100%', height: '100%',
              objectFit: 'cover', objectPosition: 'center 22%',
              transition: 'transform 0.4s ease',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(180deg, rgba(10,50,109,0.05) 0%, rgba(10,50,109,0.25) 50%, rgba(8,28,58,0.9) 100%)',
            }}
          />
          <Box sx={{ position: 'absolute', top: { xs: 8, sm: 10 }, right: { xs: 8, sm: 10 } }}>
            {liveBadge}
          </Box>
          <Box sx={{ position: 'absolute', left: { xs: 12, sm: 16 }, right: { xs: 12, sm: 16 }, bottom: { xs: 8, sm: 9 } }}>
            <Typography
              sx={{
                color: '#fbbf24', fontWeight: 800, fontSize: { xs: 8.5, sm: 9 },
                letterSpacing: 1.4, textTransform: 'uppercase', lineHeight: 1,
              }}
            >
              Featuring
            </Typography>
            <Typography
              sx={{
                color: 'white', fontWeight: 800, fontSize: { xs: 13, sm: 14, md: 15 }, lineHeight: 1.35, mt: 0.3,
                textShadow: '0 1px 6px rgba(0,0,0,0.55)',
              }}
              noWrap
            >
              {event.personalityName}
            </Typography>
          </Box>
        </Box>
      )}

      <Box
        sx={{
          p: { xs: 2, sm: 2.5 }, pb: 1.5, flex: 1, minWidth: 0,
          display: 'flex', flexDirection: 'column',
          ...(hasPersonalityPhoto && { pt: { xs: 1.5, sm: 1.75 } }),
        }}
      >
        {/* Header: event name + schedule, with actions alongside */}
        <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1} mb={1.25}>
          <Box minWidth={0} overflow="hidden" flex={1}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, color: '#1e3a5f', lineHeight: 1.3, mb: 0.3 }}
              noWrap
            >
              {name}
            </Typography>
            <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
              <EventOutlinedIcon sx={{ fontSize: 13, color: '#85a5c6' }} />
              <Typography variant="caption" sx={{ color: '#85a5c6', fontWeight: 500, fontSize: 11 }}>
                {formatSchedule(event.scheduledStart, event.scheduledEnd)}
              </Typography>
            </Box>
          </Box>

          {!preview && (isLocked || showActions) && (
            <Box display="flex" gap={0.5} flexShrink={0}>
              {isLocked && (
                <Tooltip title="View (read-only)">
                  <IconButton
                    size="small"
                    onClick={(e) => { e.currentTarget.blur(); e.stopPropagation(); onViewReadonly?.(event); }}
                    sx={{ '&:hover': { bgcolor: '#EAF0F6' } }}
                  >
                    <VisibilityIcon sx={{ fontSize: 15, color: '#5383b3' }} />
                  </IconButton>
                </Tooltip>
              )}
              {showActions && (
                <>
                  <Tooltip title={editTooltip}>
                    <span>
                      <IconButton
                        size="small"
                        disabled={isLocked}
                        onClick={(e) => { e.currentTarget.blur(); e.stopPropagation(); onEdit(event); }}
                        sx={{ '&:hover': { bgcolor: '#EAF0F6' } }}
                      >
                        <EditIcon sx={{ fontSize: 15, color: isLocked ? '#ccc' : '#0a326d' }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={deleteTooltip}>
                    <span>
                      <IconButton
                        size="small"
                        disabled={deleteDisabled}
                        onClick={handleDeleteClick}
                        sx={{ '&:hover': { bgcolor: '#fff0f0' } }}
                      >
                        <DeleteIcon sx={{ fontSize: 15, color: deleteDisabled ? '#ccc' : '#d32f2f' }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </>
              )}
            </Box>
          )}
        </Box>

        {/* Personality attached but no photo yet — small pill fallback instead of the banner */}
        {hasPersonality && !hasPersonalityPhoto && (
          <Box sx={{ ...pillSx, bgcolor: '#fffbeb', color: '#b45309', alignSelf: 'flex-start', mb: 1 }}>
            <span aria-hidden="true">★</span>
            <span>Featuring {event.personalityName}</span>
          </Box>
        )}

        {/* Stats pills */}
        <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap" mb={1.25}>
          {!hasPersonalityPhoto && liveBadge}
          <Box sx={pillSx}>
            <GroupsOutlinedIcon sx={{ fontSize: 13, color: '#94a3b8' }} />
            {event.capacity == null ? 'Unlimited' : `Capacity: ${event.capacity}`}
          </Box>
          {formatLabel != null && (
            <Box sx={pillSx}>
              <CategoryOutlinedIcon sx={{ fontSize: 13, color: '#94a3b8' }} />
              {formatLabel}
            </Box>
          )}
        </Box>

        {/* Approval action row — pinned to the bottom of the card so every card in a grid
            row lines up evenly regardless of how much content sits above it. */}
        {!preview && (
          <Box
            sx={{
              mt: 'auto', pt: 1.5, borderTop: '1px solid #EAF0F6',
              display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
            }}
          >
            {event.approvalId ? (
              <>
                <ApprovalStatusBadge
                  status={event.contentStatus ?? event.approvalStatus}
                  actionType={event.approvalActionType}
                />
                {(event.approvalStatus === 'pending' || event.approvalStatus === 'rejected') && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<VisibilityIcon sx={{ fontSize: 13 }} />}
                    onClick={(e) => { e.stopPropagation(); onViewProposal?.(event); }}
                    sx={viewBtnSx}
                  >
                    View
                  </Button>
                )}
                {showActions && event.approvalStatus === 'pending' && (
                  <WithdrawButton approvalId={event.approvalId} onWithdrawn={() => onSuccess?.(event.approvalActionType === 'create' ? 'draft' : 'live')} />
                )}
                {showActions && event.approvalStatus === 'rejected' && (
                  <WithdrawButton approvalId={event.approvalId} onWithdrawn={() => onSuccess?.(event.approvalActionType === 'create' ? 'draft' : 'live')} variant="dismiss" />
                )}
              </>
            ) : (
              event.contentStatus === 'draft' && showActions && (
                <SubmitForApprovalButton
                  contentType={CONTENT_TYPES.EVENT}
                  contentId={event.id}
                  contentName={name}
                  onSubmitted={() => onSuccess?.('pending')}
                />
              )
            )}
          </Box>
        )}
      </Box>

      {deliveryUrl && (
        <Box sx={{ borderTop: '1px solid #EAF0F6', px: 2.5, py: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          {!preview && (
            <Tooltip title="Open Delivery URL">
              <IconButton
                size="small"
                onClick={handleOpenUrl}
                sx={{ flexShrink: 0, '&:hover': { bgcolor: '#EAF0F6' } }}
              >
                <OpenInNewIcon sx={{ fontSize: 14, color: '#5383b3' }} />
              </IconButton>
            </Tooltip>
          )}
          <Typography
            variant="caption"
            sx={{ color: '#85a5c6', fontWeight: 500, fontSize: 11, flex: 1, minWidth: 0, cursor: preview ? 'default' : 'pointer' }}
            noWrap
            onClick={preview ? undefined : handleOpenUrl}
          >
            {deliveryUrl}
          </Typography>
        </Box>
      )}
    </Card>
  );
};

export default EventCard;
